import { inject, Injectable, signal, computed } from '@angular/core';
import { StorageService } from '@luzula/luzula-lib';
import changelogData from '../../assets/changelog.json';

export interface ChangelogEntry {
	type: 'neu' | 'fix' | 'verbesserung';
	text: string;
}

export interface ChangelogVersion {
	version: string;
	date: string;
	changes: ChangelogEntry[];
}

export interface Changelog {
	versions: ChangelogVersion[];
}

const STORAGE_KEY = 'lastSeenChangelogVersion';

@Injectable({
	providedIn: 'root'
})
export class ChangelogService {
	private _lastSeenVersion = signal<string | null>(null);
	private _changelog = changelogData as Changelog;

	readonly currentVersion = computed(() => this._changelog.versions[0]?.version || '0.0.0');
	readonly hasUnread = computed(() => {
		const lastSeen = this._lastSeenVersion();
		const current = this.currentVersion();
		return lastSeen !== current;
	});

	readonly versions = computed(() => this._changelog.versions);

	private storage = inject(StorageService);

	constructor() {
		this.loadLastSeenVersion();
	}

	private async loadLastSeenVersion() {
		const stored = await this.storage.get(STORAGE_KEY);
		if (stored) {
			this._lastSeenVersion.set(stored as string);
		}
	}

	async markAsRead() {
		const current = this.currentVersion();
		this._lastSeenVersion.set(current);
		await this.storage.set(STORAGE_KEY, current);
	}

	getTypeIcon(type: string): string {
		switch (type) {
			case 'neu': return '✨';
			case 'fix': return '🐛';
			case 'verbesserung': return '⚡';
			default: return '📝';
		}
	}

	getTypeLabel(type: string): string {
		switch (type) {
			case 'neu': return 'Neu';
			case 'fix': return 'Fehlerbehebung';
			case 'verbesserung': return 'Verbesserung';
			default: return 'Änderung';
		}
	}
}
